import { useState, useMemo, useEffect } from "react";
import Swal from "sweetalert2";
import StudentTemplate from "../../component/StudentTemplate";
import Select from "react-select";
import Api from "../../component/Api";
import { toast, ToastContainer } from "react-toastify";

const SupplementaryApplication = () => {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examCourses, setExamCourses] = useState({}); // stores fetched courses per exam
  const [courses, setCourses] = useState([]); // courses to render for selected exam
  const [selectedCourses, setSelectedCourses] = useState({});
  const [loadingExams, setLoadingExams] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [error, setError] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeEventData, setActiveEventData] = useState(null);
  const [isEventActive, setIsEventActive] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [hasPaidCourses, setHasPaidCourses] = useState(false);
  const [canViewComponent, setCanViewComponent] = useState(false);

  const { http } = Api();

  useEffect(() => {
    http
      .get(`getActiveEvents`, { params: { event_id: 27 } })
      .then((response) => {
        const eventstatus = response.data[0].status;
        if (eventstatus === "closed") {
          toast.error("Active Event is closed");
          setIsEventActive(false);
          // Don't set canViewComponent here - let fetchCourses determine it
        } else {
          setActiveEventData(response.data[0]);
          setIsEventActive(true);
          setCanViewComponent(true);
        }
      })
      .catch((error) => {
        console.error("Error in get active events:", error);
        toast.error("Error in get active events:");
        setIsEventActive(false);
      });
  }, []);

  // 1️⃣ Fetch exams
  useEffect(() => {
    const fetchExams = async () => {
      setLoadingExams(true);
      try {
        const { http } = Api();
        const response = await http.get("/Student/Supplyexams");
        const data = response.data;
        const formattedExams = data.result.map((exam) => ({
          value: exam.examid,
          label: exam.examtitle,
          isSupply: exam.is_supply === "1",
          isBetterment: exam.is_betterment === "1",
        }));

        setExams(formattedExams);
      } catch (err) {
        console.error("Error fetching exams:", err);
        setError("Failed to fetch exams");
      } finally {
        setLoadingExams(false);
      }
    };

    fetchExams();
  }, []);

  useEffect(() => {
    if (!selectedExam?.value) return;

    const fetchCourses = async () => {
      setLoadingCourses(true);
      try {
        const { http } = Api();
        const response = await http.get("/Student/SupplyExamsCourses", {
          params: { examid: selectedExam.value },
        });

        const data = response.data.result || [];

        // Check if there are any paid courses
        const hasPaidCourses = data.some(
          (course) => course.payment_status === 1
        );

        // Update state to allow viewing component if student has paid courses
        setHasPaidCourses(hasPaidCourses);
        if (hasPaidCourses) {
          setCanViewComponent(true);
        }

        // Filter based on payment_status
        let filteredData;
        if (hasPaidCourses) {
          filteredData = data.filter((course) => course.payment_status === 1);
        } else {
          filteredData = data.filter((course) => course.payment_status !== 1);
        }

        // Add examType field
        const formattedCourses = filteredData.map((course) => ({
          ...course,
          examType:
            course.regtype === "T"
              ? "Theory"
              : course.regtype === "P"
              ? "Practical"
              : "Theory",
        }));

        // 🟢 Sort logic — failed subjects first, then by course_code
        const sortedCourses = formattedCourses.sort((a, b) => {
          const aIsFail =
            a.theory_result === "Fail" || a.practical_result === "Fail";
          const bIsFail =
            b.theory_result === "Fail" || b.practical_result === "Fail";

          // 1️⃣ Fail subjects first
          if (aIsFail && !bIsFail) return -1;
          if (!aIsFail && bIsFail) return 1;

          // 2️⃣ If both same status (both fail or both pass), sort by course_code
          return a.course_code.localeCompare(b.course_code);
        });

        // Save in state
        setCourses(sortedCourses);

        // Only auto-select if no paid courses
        if (!hasPaidCourses) {
          const preSelectedCourses = {};
          sortedCourses.forEach((course) => {
            if (course.selected === 1) {
              const key = `${course.id}-${course.examType}`;
              if (course.appear_type === 2) {
                preSelectedCourses[key] = "supplementary";
              } else if (course.appear_type === 3) {
                preSelectedCourses[key] = "betterment";
              }
            }
          });
          setSelectedCourses(preSelectedCourses);
        }

        // Cache courses per exam
        setExamCourses((prev) => ({
          ...prev,
          [selectedExam.value]: sortedCourses,
        }));
      } catch (err) {
        console.error("Error fetching courses:", err);
        setError("Failed to fetch courses");
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [selectedExam]);

  // 3️⃣ Handle exam selection
  const handleExamChange = (option) => {
    // Only reset if switching to a different exam
    if (selectedExam?.value !== option?.value) {
      setSelectedCourses({}); // reset selections when exam changes
    }

    setSelectedExam(option);

    // 🧠 Save exam ID to localStorage (or clear if none selected)
    if (option?.value) {
      localStorage.setItem("selectedExamId", option.value);
    } else {
      localStorage.removeItem("selectedExamId");
    }
  };

  useEffect(() => {
    const storedExamId = localStorage.getItem("selectedExamId");

    if (storedExamId && exams.length > 0) {
      const matchedExam = exams.find(
        (exam) => String(exam.value) === String(storedExamId)
      );

      if (matchedExam) {
        setSelectedExam(matchedExam);
      }
    }
  }, [exams]);

  const { selectedCount, selectedCoursesList } = useMemo(() => {
    const list = Object.entries(selectedCourses)
      .filter(([_, type]) => type !== null)
      .map(([key, type]) => {
        // key format: "id-examType"
        const [id, examType] = key.split("-");
        const course = courses.find(
          (c) => c.id === parseInt(id) && c.examType === examType
        );
        return course ? { ...course, applicationType: type } : null;
      })
      .filter(Boolean);

    return {
      selectedCount: list.length,
      selectedCoursesList: list,
    };
  }, [selectedCourses, courses]);

  const handleCheckboxChange = (key, type) => {
    setSelectedCourses((prev) => {
      // If clicking the same type again, unselect it
      if (prev[key] === type) {
        const { [key]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: type };
    });
  };

  // Rename the function to handleSaveDetails
  // This function just opens the modal (instant)
  const handleOpenPreviewAndSave = async () => {
    if (selectedCoursesList.length === 0) {
      toast.error("Please select at least one course");
      return;
    }

    setIsSaving(true);
    setTotalAmount(0); // ✅ Reset amount before attempting save

    try {
      await handleSaveDetails();
      setShowPreviewModal(true); // ✅ Only open modal if save succeeds
    } catch (error) {
      console.error("Error saving details:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to save application. Please try again."
      );
      // ✅ Modal won't open, amount stays at 0
    } finally {
      setIsSaving(false);
    }
  };
  // Rename and update handleSaveDetails - this runs when clicking Pay inside modal
  const handleSaveDetails = async () => {
    // ❌ Remove setIsSaving from here - it's handled by parent now

    if (!selectedExam?.value) {
      throw new Error("Please select an exam");
    }

    if (selectedCoursesList.length === 0) {
      throw new Error("Please select at least one course");
    }

    // Prepare the payload
    const formattedCourses = selectedCoursesList.map((course) => ({
      id: course.id,
      course_code: course.course_code,
      cname: course.cname,
      college_course_type: course.college_course_type,
      course_name: course.course_name,
      course_group_id: course.course_group_id,
      group_name: course.group_name,
      discipline_name: course.discipline_name,
      Total_Mark: course.Total_Mark,
      result: course.result,
      grade: course.grade,
      theory_pract: course.theory_pract,
      theory_result: course.theory_result,
      practical_result: course.practical_result,
      regtype_1: course.regtype_1,
      regtype:
        course.examType === "Theory"
          ? "T"
          : course.examType === "Practical"
          ? "P"
          : course.regtype || "T",
      appear_type:
        course.applicationType === "supplementary"
          ? "2"
          : course.applicationType === "betterment"
          ? "3"
          : course.appear_type || "2",
      scheme_structure_id: course.scheme_structure_id,
      semesterid: course.semesterid,
    }));

    const payload = {
      courses: formattedCourses,
      examid: selectedExam.value,
    };

    const { http } = Api();
    const response = await http.post("/Student/registerExam", payload);

    // ✅ Validate response
    if (!response.data?.totalAmount || response.data.totalAmount <= 0) {
      throw new Error("Invalid fee amount received from server");
    }

    setTotalAmount(response.data.totalAmount);
    return response.data;
  };

  const handlePayment = async () => {
    try {
      // ✅ Add validation for amount
      if (!totalAmount || totalAmount <= 0) {
        toast.error("Invalid amount. Please try again.");
        setShowPreviewModal(false);
        return;
      }

      if (!selectedExam?.value) {
        toast.error("Exam not selected");
        return;
      }

      if (selectedCoursesList.length === 0) {
        toast.error("No courses selected");
        return;
      }

      // Show SweetAlert2 confirmation modal
      const result = await Swal.fire({
        title: "Confirm Payment",
        html: `
          <p class="fw-bold mb-2">
            You are about to make a payment of <strong>₹${totalAmount}</strong>.
          </p>
          <p class="mb-0">Do you wish to continue with the payment?</p>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Proceed to Payment",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        focusCancel: true,
      });

      if (!result.isConfirmed) return;

      setIsSaving(true);

      const { http } = Api();
      const res = await http.post("/Student/PaymentBystudent", {
        examid: selectedExam.value,
        amount: totalAmount,
      });

      if (res.data?.url && res.data?.reqParam) {
        const { url, reqParam } = res.data;

        const form = document.createElement("form");
        form.method = "POST";
        form.action = url;
        form.id = "frmpaymentaction";

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "epay_req_params";
        input.value = reqParam;

        form.appendChild(input);
        document.body.appendChild(form);

        form.submit();
      } else {
        toast.error(res.data?.message || "Payment initiation failed!");
        setIsSaving(false);
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error.response?.data?.message || "Failed to proceed with payment!"
      );
      setIsSaving(false);
    }
  };

  return (
    <StudentTemplate>
      <ToastContainer />
      <div className="col-12 mb-0 order-5 order-md-5">
        <div className="card tranaction-card">
          <div className="card-header card-no-border">
            <div className="d-flex justify-content-center align-items-center mb-3 mb-md-4">
              <h3 className="card-title text-center mb-2 font-weight-bold text-black mobile">
                Supplementary/Betterment Examination Registration
              </h3>
            </div>

            <div className="row">
              <div className="col-12">
                <Select
                  options={exams}
                  value={selectedExam}
                  onChange={handleExamChange}
                  placeholder="-- Select Exam --"
                  isClearable
                  className="w-100"
                  classNamePrefix="select"
                />
              </div>
            </div>
          </div>

          {selectedExam && courses.length > 0 && canViewComponent && (
            <div className="card-body">
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Sl No</th>
                        <th>Course Code</th>
                        <th>Course Name</th>
                        <th>Exam Type</th>
                        <th>Result</th>
                        <th>Grade</th>
                        {courses.length > 0 &&
                        courses[0].payment_status === 1 ? (
                          // Show Status and Type columns for submitted courses
                          <>
                            <th className="text-center">Application Type</th>
                            <th className="text-center">Status</th>
                          </>
                        ) : (
                          // Show Supplementary and Betterment columns for new applications
                          <>
                            <th className="text-center">Supplementary</th>
                            <th className="text-center">Betterment</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map((course, index) => (
                        <CourseRow
                          key={`${course.id}-${course.examType}`}
                          course={course}
                          selectedType={
                            selectedCourses[`${course.id}-${course.examType}`]
                          }
                          selectedCourses={selectedCourses}
                          onCheckboxChange={handleCheckboxChange}
                          selectedExam={selectedExam}
                          index={index}
                          isEventActive={isEventActive}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="d-block d-md-none">
                {courses.map((course, index) => (
                  <CourseCard
                    key={`${course.course_code}-${course.examType}-${index}`}
                    course={course}
                    selectedCourses={selectedCourses}
                    onCheckboxChange={handleCheckboxChange}
                    selectedExam={selectedExam}
                    isEventActive={isEventActive}
                    index={index}
                  />
                ))}
              </div>

              {courses.length > 0 && courses[0].payment_status !== 1 && (
                <div className="d-none d-md-flex justify-content-between align-items-center mt-4 p-3 bg-light rounded">
                  <div className="d-flex align-items-center gap-4">
                    <h6 className="mb-0" style={{ color: "#495057" }}>
                      Selected Courses: <strong>{selectedCount}</strong>
                    </h6>
                  </div>
                  <button
                    className="btn btn-primary btn-md"
                    disabled={selectedCoursesList.length === 0 || isSaving}
                    onClick={handleOpenPreviewAndSave}
                    aria-label="Preview application and pay"
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving...
                      </>
                    ) : (
                      "Preview & Pay"
                    )}
                  </button>
                </div>
              )}

              {/* Mobile Sticky Footer - OUTSIDE card-body */}
              {selectedExam &&
                courses.length > 0 &&
                courses[0].payment_status !== 1 && (
                  <div
                    className="d-md-none position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg p-3"
                    style={{
                      zIndex: 1030,
                      left: 0,
                      right: 0,
                    }}
                  >
                    <div className="container-fluid">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-3">
                          <span className="text-muted small">Selected:</span>
                          <span
                            className="badge bg-primary rounded-pill"
                            style={{
                              fontSize: "0.9rem",
                              padding: "0.4rem 0.8rem",
                            }}
                          >
                            {selectedCount}
                          </span>
                        </div>
                      </div>
                      <button
                        className="btn btn-primary w-100 py-2 fw-semibold"
                        disabled={selectedCoursesList.length === 0 || isSaving}
                        onClick={handleOpenPreviewAndSave}
                        style={{ fontSize: "1rem" }}
                      >
                        {isSaving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Saving...
                          </>
                        ) : (
                          "Preview & Pay"
                        )}
                      </button>
                    </div>
                  </div>
                )}

              {/* Mobile spacer */}
              {selectedExam &&
                courses.length > 0 &&
                courses[0].payment_status !== 1 && (
                  <div className="d-md-none" style={{ height: "10px" }}></div>
                )}
            </div>
          )}

          {selectedExam && loadingCourses && (
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-3">Loading courses...</p>
            </div>
          )}

          {selectedExam && courses.length === 0 && !loadingCourses && (
            <div className="card-body text-center py-5">
              <p className="text-muted">No courses found for this exam.</p>
            </div>
          )}

          {!selectedExam && (
            <div className="card-body text-center py-5">
              <p className="text-muted">
                Please select an exam to view courses.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          style={{
            display: "flex",
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1050,
            padding: "1rem",
            boxSizing: "border-box",
          }}
        >
          <div
            className="modal-dialog"
            style={{
              width: "100%",
              maxWidth: "900px",
              margin: "auto",
              height: "90vh",
              maxHeight: "125vh",
            }}
          >
            <div
              className="modal-content"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                width: "100%",
                borderRadius: "0.5rem",
              }}
            >
              {/* Header */}
              <div
                className="modal-header responsive-modal-header border-bottom"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "#fff",
                }}
              >
                <h5 className="modal-title fw-bold">Preview Application</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPreviewModal(false)}
                  aria-label="Close"
                ></button>
              </div>

              {/* Body */}
              <div
                className="modal-body overflow-auto p-4"
                style={{ backgroundColor: "#f8f9fa", flex: 1 }}
              >
                {/* Selected Examination */}
                <div className="mb-4 d-flex align-items-center justify-content-start flex-wrap gap-2">
                  <h6
                    className="text-muted mb-0 me-2 fw-normal"
                    style={{ lineHeight: "1.2", fontSize: "1rem" }}
                  >
                    Examination:
                  </h6>
                  <h6 className="fw-bold mb-0" style={{ lineHeight: "1.2" }}>
                    {selectedExam?.label}
                  </h6>
                </div>

                {/* Applied Courses Section */}
                <div className="mb-3 border rounded p-3 bg-white shadow-sm">
                  <h6 className="fw-semibold mb-3 d-flex align-items-center">
                    <i className="fa fa-book me-2 text-primary"></i>
                    Applied Courses
                  </h6>

                  <div className="row g-3">
                    {selectedCoursesList.map((course, index) => (
                      <div
                        key={`${course.course_code}-${course.examType}`}
                        className="col-12 col-md-6 d-flex"
                      >
                        <div className="p-3 bg-light border rounded flex-fill d-flex flex-column position-relative modal-course-card">
                          {/* Top-right condonation badge */}
                          {course.condonation_time && (
                            <span
                              className="badge bg-warning text-dark position-absolute"
                              style={{
                                top: "0.5rem",
                                right: "0.5rem",
                                fontSize: "0.65rem",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={course.condonation_time}
                            >
                              {course.condonation_time}
                            </span>
                          )}

                          {/* Header badges */}
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <span className="badge bg-primary rounded-pill small shadow-sm">
                              #{index + 1}
                            </span>
                            <span
                              className={`badge small ${
                                course.applicationType === "supplementary"
                                  ? "bg-warning text-dark"
                                  : "bg-info text-white"
                              }`}
                            >
                              {course.applicationType === "supplementary"
                                ? "Supplementary"
                                : "Betterment"}
                            </span>
                          </div>

                          {/* Course Code */}
                          <div
                            className="fw-semibold small text-dark text-wrap"
                            style={{
                              wordBreak: "break-word",
                              minHeight: "2.4em",
                            }}
                          >
                            {course.course_code}
                          </div>

                          {/* Course Name */}
                          <div
                            className="text-muted flex-grow-1"
                            style={{
                              fontSize: "0.8rem",
                              lineHeight: "1.3",
                              wordBreak: "break-word",
                              overflow: "hidden",
                            }}
                          >
                            {course.cname}
                          </div>

                          {/* Exam Type */}
                          <div
                            className="text-muted mt-1"
                            style={{
                              fontSize: "0.75rem",
                              fontStyle: "italic",
                            }}
                          >
                            {course.examType} Exam
                          </div>

                          {/* Total Marks and Grade */}
                          <div
                            className="d-flex justify-content-between mt-2 text-muted flex-grow-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <span>Grade: {course.grade}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="modal-footer"
                style={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 1,
                  backgroundColor: "white",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                {/* Compact warning message */}
                <div
                  className="text-start text-danger fw-semibold"
                  style={{
                    fontSize: "0.8rem",
                    marginBottom: "0.4rem",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    backgroundColor: "rgba(220, 53, 69, 0.1)",
                  }}
                >
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Please note: You can submit this application only once during
                  the application window. Review all details carefully before
                  proceeding.
                </div>
                {/* Action buttons */}
                <div className="d-flex justify-content-end w-100 gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPreviewModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handlePayment}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Processing...
                      </>
                    ) : (
                      `Pay ₹${totalAmount}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </StudentTemplate>
  );
};

// Separate component for better performance
const CourseRow = ({
  course,
  selectedType,
  onCheckboxChange,
  selectedExam,
  selectedCourses,
  index,
  isEventActive,
}) => {
  const key = `${course.id}-${course.examType}`;
  const isSupplementaryChecked = selectedCourses[key] === "supplementary";
  const isBettermentChecked = selectedCourses[key] === "betterment";
  const isFail =
    (course.examType === "Theory" && course.theory_result === "Fail") ||
    (course.examType === "Practical" && course.practical_result === "Fail");
  const isPractical = course.examType === "Practical";
  const isMalpractice = course.malpractice_status === 1;

  // Check if course is submitted (payment done)
  const isSubmitted = course.payment_status === 1;

  return (
    <tr>
      <td>{index + 1}</td>
      <td>{course.course_code}</td>
      <td>{course.cname}</td>
      <td className="text-center">{course.examType}</td>
      <td className="text-center">
        <span className={isFail ? "text-danger" : "text-success"}>
          {course.examType === "Theory"
            ? course.theory_result
            : course.practical_result}
        </span>
      </td>

      <td className="text-center">{course.grade}</td>

      {isSubmitted ? (
        // Show Application Type and Status columns if payment is done
        <>
          <td className="text-center">
            <span
              className={`badge ${
                course.appear_type === 2
                  ? "bg-warning text-dark"
                  : "bg-info text-white"
              }`}
            >
              {course.appear_type === 2 ? "Supplementary" : "Betterment"}
            </span>
          </td>
          <td className="text-center">
            <span className="badge bg-success">Application Submitted</span>
          </td>
        </>
      ) : (
        // Show checkboxes if payment not done and event is active
        <>
          <td className="text-center">
            <input
              type="checkbox"
              className="form-check-input"
              style={{
                width: "14px",
                height: "14px",
                cursor:
                  isFail &&
                  selectedExam?.isSupply &&
                  isEventActive &&
                  !isMalpractice
                    ? "pointer"
                    : "not-allowed",
                border: "2px solid #6c757d",
                borderRadius: "4px",
              }}
              disabled={
                !(isFail && selectedExam?.isSupply && isEventActive) ||
                isMalpractice
              }
              checked={isSupplementaryChecked}
              onChange={() =>
                onCheckboxChange(
                  `${course.id}-${course.examType}`,
                  "supplementary"
                )
              }
              aria-label={`Supplementary for ${course.cname}`}
            />
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              className="form-check-input"
              style={{
                width: "14px",
                height: "14px",
                cursor:
                  !isFail &&
                  !isPractical &&
                  selectedExam?.isBetterment &&
                  isEventActive &&
                  !isMalpractice
                    ? "pointer"
                    : "not-allowed",
                border: "2px solid #6c757d",
                borderRadius: "4px",
              }}
              disabled={
                isMalpractice ||
                isFail ||
                isPractical ||
                !selectedExam?.isBetterment ||
                !isEventActive
              }
              checked={isBettermentChecked}
              onChange={() =>
                onCheckboxChange(
                  `${course.id}-${course.examType}`,
                  "betterment"
                )
              }
              aria-label={`Betterment for ${course.cname}`}
            />
          </td>
        </>
      )}
    </tr>
  );
};

const CourseCard = ({
  course,
  selectedType,
  onCheckboxChange,
  selectedExam,
  selectedCourses,
  index,
  isEventActive,
}) => {
  const key = `${course.id}-${course.examType}`;
  const isSupplementaryChecked = selectedCourses[key] === "supplementary";
  const isBettermentChecked = selectedCourses[key] === "betterment";
  const isFail =
    (course.examType === "Theory" && course.theory_result === "Fail") ||
    (course.examType === "Practical" && course.practical_result === "Fail");
  const isPractical = course.examType === "Practical";
  const hasSelection = isSupplementaryChecked || isBettermentChecked;
  const isMalpractice = course.malpractice_status === 1;

  // Check if course is submitted (payment done)
  const isSubmitted = course.payment_status === 1;

  return (
    <div
      className={`card mb-3 shadow-sm ${
        hasSelection
          ? "border-primary border-2"
          : isSubmitted
          ? "border-success border-2"
          : "border"
      }`}
      style={{
        transition: "all 0.2s ease",
        transform: hasSelection || isSubmitted ? "scale(1.02)" : "scale(1)",
      }}
    >
      <div className="card-body p-3">
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <span
            className="badge bg-secondary"
            style={{ fontSize: "0.7rem", padding: "0.35rem 0.6rem" }}
          >
            #{index + 1}
          </span>
          <span
            className={`badge ${isFail ? "bg-danger" : "bg-success"}`}
            style={{ fontSize: "0.7rem", padding: "0.35rem 0.6rem" }}
          >
            {course.examType === "Theory"
              ? course.theory_result || course.result
              : course.practical_result || course.result}
          </span>
        </div>

        {/* Course Info */}
        <div className="mb-2 d-flex" style={{ alignItems: "baseline" }}>
          <span
            className="text-muted"
            style={{ minWidth: "90px", fontSize: "0.7rem", fontWeight: 500 }}
          >
            Course Code:
          </span>
          <span className="fw-semibold" style={{ fontSize: "0.8rem", flex: 1 }}>
            {course.course_code}
          </span>
        </div>
        <div className="d-flex" style={{ alignItems: "baseline" }}>
          <span
            className="text-muted"
            style={{ minWidth: "90px", fontSize: "0.7rem", fontWeight: 500 }}
          >
            Course Name:
          </span>
          <span
            style={{
              fontSize: "0.8rem",
              flex: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {course.cname}
          </span>
        </div>
        <div
          className="d-flex"
          style={{ alignItems: "baseline", marginTop: "4px" }}
        >
          <span
            className="text-muted"
            style={{ minWidth: "90px", fontSize: "0.7rem", fontWeight: 500 }}
          >
            Exam Type:
          </span>
          <span style={{ fontSize: "0.8rem", flex: 1 }}>{course.examType}</span>
        </div>
        <div
          className="d-flex"
          style={{ alignItems: "baseline", marginTop: "4px" }}
        >
          <span
            className="text-muted"
            style={{ minWidth: "90px", fontSize: "0.7rem", fontWeight: 500 }}
          >
            Grade:
          </span>
          <span style={{ fontSize: "0.8rem", flex: 1 }}>{course.grade}</span>
        </div>

        {isSubmitted ? (
          // Show Application Type and Status if payment is done
          <div className="border-top pt-3 mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span
                className="text-muted small"
                style={{ fontSize: "0.75rem", fontWeight: 500 }}
              >
                Application Type:
              </span>
              <span
                className={`badge ${
                  course.appear_type === 2
                    ? "bg-warning text-dark"
                    : "bg-info text-white"
                }`}
                style={{ fontSize: "0.75rem" }}
              >
                {course.appear_type === 2 ? "Supplementary" : "Betterment"}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span
                className="text-muted small"
                style={{ fontSize: "0.75rem", fontWeight: 500 }}
              >
                Status:
              </span>
              <span
                className="badge bg-success"
                style={{ fontSize: "0.75rem" }}
              >
                <i className="fa fa-check-circle me-1"></i>
                Application Submitted
              </span>
            </div>
          </div>
        ) : (
          // Show selection options if payment not done and event is active
          <div className="border-top pt-3 mt-3">
            <div className="row g-2">
              {/* Supplementary Option */}
              <div className="col-6">
                <div
                  className={`border rounded p-3 text-center position-relative ${
                    isSupplementaryChecked ? "border-primary" : ""
                  }`}
                  style={{
                    backgroundColor: isSupplementaryChecked
                      ? "#e7f3ff"
                      : "#f8f9fa",
                    cursor:
                      !isMalpractice &&
                      isFail &&
                      selectedExam?.isSupply &&
                      isEventActive
                        ? "pointer"
                        : "not-allowed",
                    opacity:
                      !isMalpractice &&
                      isFail &&
                      selectedExam?.isSupply &&
                      isEventActive
                        ? 1
                        : 0.6,
                    transition: "all 0.2s ease",
                  }}
                  onClick={(e) => {
                    if (
                      e.target.tagName !== "INPUT" &&
                      !isMalpractice &&
                      isFail &&
                      selectedExam?.isSupply &&
                      isEventActive
                    ) {
                      onCheckboxChange(
                        `${course.id}-${course.examType}`,
                        "supplementary"
                      );
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSupplementaryChecked}
                    disabled={
                      isMalpractice ||
                      !(isFail && selectedExam?.isSupply && isEventActive)
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      onCheckboxChange(
                        `${course.id}-${course.examType}`,
                        "supplementary"
                      );
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="form-check-input position-absolute"
                    style={{
                      top: "8px",
                      right: "8px",
                      width: "18px",
                      height: "18px",
                      cursor:
                        !isMalpractice &&
                        isFail &&
                        selectedExam?.isSupply &&
                        isEventActive
                          ? "pointer"
                          : "not-allowed",
                      border: "2px solid #6c757d",
                      borderRadius: "4px",
                    }}
                    aria-label={`Supplementary for ${course.cname}`}
                  />
                  <div className="mt-2">
                    <i
                      className="fa fa-solid fa-repeat text-warning mb-2"
                      style={{ fontSize: "1.2rem" }}
                    ></i>
                    <div className="fw-semibold small">Supplementary</div>
                  </div>
                </div>
              </div>

              {/* Betterment Option */}
              <div className="col-6">
                <div
                  className={`border rounded p-3 text-center position-relative ${
                    isBettermentChecked ? "border-primary" : ""
                  }`}
                  style={{
                    backgroundColor: isBettermentChecked
                      ? "#e7f3ff"
                      : "#f8f9fa",
                    cursor:
                      !isMalpractice &&
                      !isFail &&
                      !isPractical &&
                      selectedExam?.isBetterment &&
                      isEventActive
                        ? "pointer"
                        : "not-allowed",
                    opacity:
                      !isMalpractice &&
                      !isFail &&
                      !isPractical &&
                      selectedExam?.isBetterment &&
                      isEventActive
                        ? 1
                        : 0.6,
                    transition: "all 0.2s ease",
                  }}
                  onClick={(e) => {
                    if (
                      e.target.tagName !== "INPUT" &&
                      !isMalpractice &&
                      !isFail &&
                      !isPractical &&
                      selectedExam?.isBetterment &&
                      isEventActive
                    ) {
                      onCheckboxChange(
                        `${course.id}-${course.examType}`,
                        "betterment"
                      );
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isBettermentChecked}
                    disabled={
                      isMalpractice ||
                      isFail ||
                      isPractical ||
                      !selectedExam?.isBetterment ||
                      !isEventActive
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      onCheckboxChange(
                        `${course.id}-${course.examType}`,
                        "betterment"
                      );
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="form-check-input position-absolute"
                    style={{
                      top: "8px",
                      right: "8px",
                      width: "18px",
                      height: "18px",
                      cursor:
                        !isMalpractice &&
                        !isFail &&
                        !isPractical &&
                        selectedExam?.isBetterment &&
                        isEventActive
                          ? "pointer"
                          : "not-allowed",
                      border: "2px solid #6c757d",
                      borderRadius: "4px",
                    }}
                    aria-label={`Betterment for ${course.cname}`}
                  />
                  <div className="mt-2">
                    <i
                      className="fa fa-solid fa-book-open-reader text-info mb-2"
                      style={{ fontSize: "1.2rem" }}
                    ></i>
                    <div className="fw-semibold small">Betterment</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplementaryApplication;
